import { useCallback, useEffect, useMemo } from 'react';
import { Platform } from 'react-native';

import { useExtractedQuestionsBubbleContext } from '@/components/chat/bubbles/extractedQuestions/ExtractedQuestionsBubbleContext';
import { QuestionChip } from '@/components/chat/bubbles/extractedQuestions/QuestionChip';
import { computeSegments } from '@/components/chat/bubbles/extractedQuestions/segments';
import { Text } from '@/components/ui/text';
import { useColors } from '@/hooks/useColors';
import type { ExtractedQuestion } from '@/types/extractedQuestions';
import { Sentry } from '@/util/sentry';

interface HighlightedMessageTextProps {
  messageSid: string;
  body: string;
  extractedQuestions: ExtractedQuestion[];
  textClassName?: string;
  position: 'left' | 'right';
  /** The recommended case layout's chip spacing and colors. Temporary with the layout switch. */
  recommended?: boolean;
}

/**
 * Renders the message body with each question's source span underlined
 * and a small `Q{n}` chip after each underlined run. Tapping either the
 * underline or the chip pulses the matching question card below. Underline color and chip palette come from the Primum
 * semantic palette so the design stays readable on either bubble background.
 */
export function HighlightedMessageText({
  messageSid,
  body,
  extractedQuestions,
  textClassName,
  position,
  recommended = false,
}: HighlightedMessageTextProps) {
  const result = useMemo(
    () => computeSegments(body, extractedQuestions),
    [body, extractedQuestions]
  );

  useEffect(() => {
    if (result.fallbackUsed) {
      Sentry.addBreadcrumb({
        category: 'extracted-questions',
        message: 'Span exceeded body length; rendered plain body',
        level: 'warning',
        data: { messageSid, bodyLength: body.length, questionCount: extractedQuestions.length },
      });
    }

    if (result.droppedQuestionIndices.length > 0) {
      Sentry.addBreadcrumb({
        category: 'extracted-questions',
        message: 'Overlapping spans dropped from inline marks',
        level: 'info',
        data: {
          messageSid,
          droppedQuestionIndices: result.droppedQuestionIndices,
        },
      });
    }
  }, [result, messageSid, body.length, extractedQuestions.length]);

  return (
    <Text className={textClassName}>
      {result.segments.map((segment, segmentIndex) => {
        if (segment.kind === 'plain') {
          return (
            <Text key={`p-${segmentIndex}`} className={textClassName}>
              {segment.text}
            </Text>
          );
        }

        return (
          <Text key={`h-${segment.questionIndex}`} className={textClassName}>
            <HighlightedRun
              questionIndex={segment.questionIndex}
              text={segment.text}
              textClassName={textClassName}
              position={position}
            />
            {recommended ? CHIP_LEAD : ' '}
            <QuestionChip
              questionIndex={segment.questionIndex}
              position={position}
              recommended={recommended}
            />
            {recommended ? CHIP_TRAIL : ' '}
          </Text>
        );
      })}
    </Text>
  );
}

interface HighlightedRunProps {
  questionIndex: number;
  text: string;
  textClassName?: string;
  position: 'left' | 'right';
}

function HighlightedRun({ questionIndex, text, textClassName, position }: HighlightedRunProps) {
  const { colors } = useColors();

  const { pulseQuestion } = useExtractedQuestionsBubbleContext();

  const underlineColor = position === 'right' ? colors.primary[300] : colors.outline[300];

  const onPress = useCallback(() => {
    pulseQuestion(questionIndex);
  }, [pulseQuestion, questionIndex]);

  // Native: nested `<Text onPress>` stays inline and fires correctly.
  // Web: `onPress` causes RN-Web to render a `<button>` (block), breaking the
  //   surrounding text flow. Pass `onClick` directly to the underlying DOM
  //   node instead — that keeps the element rendered inline as a `<div>`.
  const pressProps = Platform.OS === 'web' ? ({ onClick: onPress } as object) : { onPress };

  return (
    <Text
      style={{ ...UNDERLINE_STYLE, ...CLICKABLE_STYLE, textDecorationColor: underlineColor }}
      className={textClassName}
      {...pressProps}>
      {text}
    </Text>
  );
}

/**
 * A narrow no-break space before the chip keeps it on the run's line and close to it, and a wider
 * gap after it, so the chip reads as the end of its own sentence rather than the start of the next.
 */
const CHIP_LEAD = '\u202F';

const CHIP_TRAIL = ' \u2002';

/** Cursor hint on web only; native ignores `cursor`. */
const CLICKABLE_STYLE = { cursor: 'pointer' } as unknown as { cursor: 'auto' };

/**
 * Dotted rather than solid, so the run reads as "there's more about this"
 * rather than as a link that will open something. iOS and web draw the dots;
 * Android draws every underline solid.
 */
const UNDERLINE_STYLE = {
  textDecorationLine: 'underline' as const,
  textDecorationStyle: 'dotted' as const,
};
